import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, Save, Plus, X, Loader2, FileText } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const Field = ({ label, children, required = false }) => (
  <div className="space-y-1">
    <Label className="text-xs font-semibold text-gray-600 font-ibm">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    {children}
  </div>
);

const ManifestForm = () => {
  const { manifestId } = useParams();
  const [searchParams] = useSearchParams();
  const docketIdParam = searchParams.get('docket_id');
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const isEdit = !!manifestId;
  const [docketId, setDocketId] = useState(docketIdParam);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    job_no: '',
    shipper_name: '',
    shipper_address: '',
    consignee_name: '',
    consignee_address: '',
    mawb_no: '',
    hawb_no: '',
    no_of_hawb: '',
    freight: '',
    flight: '',
    manifest_date: new Date().toISOString().split('T')[0]
  });
  const [packages, setPackages] = useState([{
    gr_weight: '',
    chr_weight: '',
    hawb_no: '',
    shipper_text: '',
    consignee_text: '',
    commodity: '',
    no_of_pieces: '',
    sort_order: 1
  }]);
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const getAuth = () => ({ 'Authorization': `Bearer ${localStorage.getItem('auth_token')}`, 'Content-Type': 'application/json' });
  
  useEffect(() => {
    const init = async () => {
      if (isEdit && manifestId) {
        await loadManifest();
      } else if (docketIdParam) {
        setDocketId(docketIdParam);
        await loadPrefillData();
      } else {
        // No docket ID and not edit mode
        setLoading(false);
        toast({ 
          title: 'Warning', 
          description: 'No docket ID provided. You can still create a manifest manually.', 
          variant: 'default' 
        });
      }
    };
    
    init();
  }, [manifestId, docketIdParam]);
  
  const loadManifest = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${backendUrl}/api/air-outward-manifests/${manifestId}`, { headers: getAuth() });
      if (!res.ok) throw new Error('Failed to load manifest');
      const data = await res.json();
      
      // Set docket ID from loaded manifest
      setDocketId(data.docket_id);
      
      setFormData({
        job_no: data.job_no || '',
        shipper_name: data.shipper_name || '',
        shipper_address: data.shipper_address || '',
        consignee_name: data.consignee_name || '',
        consignee_address: data.consignee_address || '',
        mawb_no: data.mawb_no || '',
        hawb_no: data.hawb_no || '',
        no_of_hawb: data.no_of_hawb || '',
        freight: data.freight || '',
        flight: data.flight || '',
        manifest_date: data.manifest_date ? data.manifest_date.split('T')[0] : ''
      });
      
      if (data.packages && data.packages.length > 0) {
        setPackages(data.packages.map(pkg => ({
          id: pkg.id,
          gr_weight: pkg.gr_weight || '',
          chr_weight: pkg.chr_weight || '',
          hawb_no: pkg.hawb_no || '',
          shipper_text: pkg.shipper_text || '',
          consignee_text: pkg.consignee_text || '',
          commodity: pkg.commodity || '',
          no_of_pieces: pkg.no_of_pieces || '',
          sort_order: pkg.sort_order || 1
        })));
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };
  
  const loadPrefillData = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${backendUrl}/api/air-outward-manifests/prefill-data?docket_id=${docketId}`, { headers: getAuth() });
      if (!res.ok) {
        console.error('Prefill failed, using defaults');
        setLoading(false);
        return;
      }
      const data = await res.json();
      
      setFormData({
        job_no: data.job_no || '',
        shipper_name: data.shipper_name || '',
        shipper_address: data.shipper_address || '',
        consignee_name: data.consignee_name || '',
        consignee_address: data.consignee_address || '',
        mawb_no: data.mawb_no || '',
        hawb_no: data.hawb_no || '',
        no_of_hawb: data.no_of_hawb || '',
        freight: data.freight || '',
        flight: data.flight || '',
        manifest_date: data.manifest_date || new Date().toISOString().split('T')[0]
      });
      
      if (data.packages && data.packages.length > 0) {
        setPackages(data.packages);
      }
    } catch (e) {
      console.error('Failed to load prefill data:', e);
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handlePackageChange = (index, field, value) => {
    const updated = [...packages];
    updated[index] = { ...updated[index], [field]: value };
    setPackages(updated);
  };
  
  const addPackageRow = () => {
    setPackages([...packages, {
      gr_weight: '',
      chr_weight: '',
      hawb_no: '',
      shipper_text: '',
      consignee_text: '',
      commodity: '',
      no_of_pieces: '',
      sort_order: packages.length + 1
    }]);
  };
  
  const removePackageRow = (index) => {
    if (packages.length === 1) {
      toast({ title: 'Warning', description: 'At least one package row is required', variant: 'destructive' });
      return;
    }
    const updated = packages.filter((_, i) => i !== index);
    setPackages(updated);
  };
  
  const handleSave = async () => {
    // Validation
    if (!formData.shipper_name || !formData.consignee_name || !formData.manifest_date) {
      toast({ title: 'Validation Error', description: 'Shipper, Consignee, and Date are required', variant: 'destructive' });
      return;
    }
    
    if (packages.length === 0) {
      toast({ title: 'Validation Error', description: 'At least one package row is required', variant: 'destructive' });
      return;
    }
    
    // Get company_id from localStorage or current user
    let companyId = localStorage.getItem('company_id');
    
    // If not in localStorage, try to get from auth token
    if (!companyId) {
      try {
        const token = localStorage.getItem('auth_token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          companyId = payload.company_id;
          // Store for future use
          if (companyId) {
            localStorage.setItem('company_id', companyId);
          }
        }
      } catch (e) {
        console.error('Failed to extract company_id from token:', e);
      }
    }
    
    // For new manifest, ensure we have both docketId and companyId
    if (!isEdit) {
      if (!docketId) {
        toast({ title: 'Error', description: 'Docket ID is required to create manifest', variant: 'destructive' });
        return;
      }
      if (!companyId) {
        toast({ title: 'Error', description: 'Company ID not found. Please login again.', variant: 'destructive' });
        return;
      }
    }
    
    try {
      setSaving(true);
      
      // Build base payload
      const basePayload = {
        job_no: formData.job_no,
        shipper_name: formData.shipper_name,
        shipper_address: formData.shipper_address,
        consignee_name: formData.consignee_name,
        consignee_address: formData.consignee_address,
        mawb_no: formData.mawb_no,
        hawb_no: formData.hawb_no,
        no_of_hawb: formData.no_of_hawb ? parseInt(formData.no_of_hawb) : null,
        freight: formData.freight,
        flight: formData.flight,
        manifest_date: formData.manifest_date,
        packages: packages.map((pkg, idx) => ({
          gr_weight: pkg.gr_weight || null,
          chr_weight: pkg.chr_weight || null,
          hawb_no: pkg.hawb_no || null,
          shipper_text: pkg.shipper_text || null,
          consignee_text: pkg.consignee_text || null,
          commodity: pkg.commodity || null,
          no_of_pieces: pkg.no_of_pieces || null,
          sort_order: idx + 1
        }))
      };
      
      // Add docket_id and company_id only for create
      let payload;
      if (isEdit) {
        payload = basePayload;
      } else {
        payload = {
          ...basePayload,
          docket_id: docketId,
          company_id: companyId
        };
      }
      
      console.log('Saving manifest:', { isEdit, payload });
      
      const url = isEdit
        ? `${backendUrl}/api/air-outward-manifests/${manifestId}`
        : `${backendUrl}/api/air-outward-manifests`;
      
      console.log('API Request:', {
        url,
        method: isEdit ? 'PUT' : 'POST',
        payloadKeys: Object.keys(payload),
        hasDocketId: 'docket_id' in payload,
        hasCompanyId: 'company_id' in payload
      });
      
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: getAuth(),
        body: JSON.stringify(payload)
      });
      
      console.log('API Response Status:', res.status);
      
      if (!res.ok) {
        const error = await res.json();
        console.error('API Error:', error);
        throw new Error(error.detail || 'Failed to save manifest');
      }
      
      const result = await res.json();
      console.log('Manifest saved successfully:', result.id);
      
      toast({ title: 'Success', description: `Manifest ${isEdit ? 'updated' : 'created'} successfully` });
      
      // Navigate back to list
      if (docketId) {
        navigate(`/dockets/${docketId}/air-manifests`);
      } else {
        navigate(-1);
      }
    } catch (e) {
      console.error('Save error:', e);
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-surface-light p-6">
      <div className="max-w-6xl mx-auto space-y-5">
        
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-text-dark font-manrope flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                {isEdit ? 'Edit Console Manifest' : 'Create Console Manifest'}
              </h1>
              <p className="text-sm text-text-secondary font-ibm">
                Job No: {formData.job_no || 'N/A'}
              </p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {saving ? 'Saving...' : 'Save Manifest'}
          </Button>
        </div>
        
        {/* Delivery Info Section */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-purple-500 px-5 py-3">
            <h2 className="text-base font-semibold text-white font-ibm">Delivery Information</h2>
          </div>
          <div className="p-5">
            <div className="grid grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                <Field label="Shipper" required>
                  <Input
                    value={formData.shipper_name}
                    onChange={e => handleChange('shipper_name', e.target.value)}
                    placeholder="Shipper Name"
                  />
                </Field>
                <Field label="Shipper Address">
                  <Textarea
                    value={formData.shipper_address}
                    onChange={e => handleChange('shipper_address', e.target.value)}
                    className="h-24"
                    placeholder="Shipper Address"
                  />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="MAWB No.">
                    <Input
                      value={formData.mawb_no}
                      onChange={e => handleChange('mawb_no', e.target.value)}
                      placeholder="MAWB No."
                    />
                  </Field>
                  <Field label="HAWB No.">
                    <Input
                      value={formData.hawb_no}
                      onChange={e => handleChange('hawb_no', e.target.value)}
                      placeholder="HAWB No."
                    />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="No. of HAWB">
                    <Input
                      type="number"
                      value={formData.no_of_hawb}
                      onChange={e => handleChange('no_of_hawb', e.target.value)}
                      placeholder="No. of HAWB"
                    />
                  </Field>
                  <Field label="Freight">
                    <Input
                      value={formData.freight}
                      onChange={e => handleChange('freight', e.target.value)}
                      placeholder="Freight"
                    />
                  </Field>
                </div>
              </div>
              
              {/* Right Column */}
              <div className="space-y-4">
                <Field label="Consignee" required>
                  <Input
                    value={formData.consignee_name}
                    onChange={e => handleChange('consignee_name', e.target.value)}
                    placeholder="Consignee Name"
                  />
                </Field>
                <Field label="Consignee Address">
                  <Textarea
                    value={formData.consignee_address}
                    onChange={e => handleChange('consignee_address', e.target.value)}
                    className="h-24"
                    placeholder="Consignee Address"
                  />
                </Field>
                <Field label="Flight">
                  <Input
                    value={formData.flight}
                    onChange={e => handleChange('flight', e.target.value)}
                    placeholder="Flight No."
                  />
                </Field>
                <Field label="Date" required>
                  <Input
                    type="date"
                    value={formData.manifest_date}
                    onChange={e => handleChange('manifest_date', e.target.value)}
                  />
                </Field>
              </div>
            </div>
          </div>
        </Card>
        
        {/* Package Info Section */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-purple-500 px-5 py-3 flex items-center justify-between">
            <h2 className="text-base font-semibold text-white font-ibm">Package Information</h2>
            <Button onClick={addPackageRow} variant="outline" size="sm" className="bg-white text-purple-600 border-white hover:bg-purple-50">
              <Plus className="w-4 h-4 mr-1" />
              Add Row
            </Button>
          </div>
          <div className="p-5 space-y-4">
            {packages.map((pkg, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 relative">
                {packages.length > 1 && (
                  <button
                    onClick={() => removePackageRow(index)}
                    className="absolute top-2 right-2 p-1 text-red-500 hover:bg-red-50 rounded"
                    title="Remove Row"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                <div className="grid grid-cols-4 gap-3 mb-3">
                  <Field label="Gr Weight">
                    <Input
                      value={pkg.gr_weight}
                      onChange={e => handlePackageChange(index, 'gr_weight', e.target.value)}
                      placeholder="Gr Weight"
                    />
                  </Field>
                  <Field label="Chr Weight">
                    <Input
                      value={pkg.chr_weight}
                      onChange={e => handlePackageChange(index, 'chr_weight', e.target.value)}
                      placeholder="Chr Weight"
                    />
                  </Field>
                  <Field label="HAWB No.">
                    <Input
                      value={pkg.hawb_no}
                      onChange={e => handlePackageChange(index, 'hawb_no', e.target.value)}
                      placeholder="HAWB No."
                    />
                  </Field>
                  <Field label="No. Of Pieces">
                    <Input
                      value={pkg.no_of_pieces}
                      onChange={e => handlePackageChange(index, 'no_of_pieces', e.target.value)}
                      placeholder="No. Of Pieces"
                    />
                  </Field>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Shipper">
                    <Textarea
                      value={pkg.shipper_text}
                      onChange={e => handlePackageChange(index, 'shipper_text', e.target.value)}
                      className="h-16"
                      placeholder="Shipper"
                    />
                  </Field>
                  <Field label="Consignee">
                    <Textarea
                      value={pkg.consignee_text}
                      onChange={e => handlePackageChange(index, 'consignee_text', e.target.value)}
                      className="h-16"
                      placeholder="Consignee"
                    />
                  </Field>
                  <Field label="Commodity">
                    <Textarea
                      value={pkg.commodity}
                      onChange={e => handlePackageChange(index, 'commodity', e.target.value)}
                      className="h-16"
                      placeholder="Commodity"
                    />
                  </Field>
                </div>
              </div>
            ))}
          </div>
        </Card>
        
      </div>
    </div>
  );
};

export default ManifestForm;
